import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ResponseError, responseErrorsResolver } from '@ui/src';
import { ApplicationDocument } from '../../../../hoc/new-application/types';
import {
  useLazyGetCofApplicationDocumentFileQuery,
  useLazyGetIndividualApplicationDocumentFileQuery,
  useRenameCofDocumentMutation,
  useRenameIndividualDocumentMutation,
} from '../../../../hoc/new-application/store/services';

const useDocuments = ({
  cof,
  applicationId,
  documents,
  onGroupSelect,
}: {
  cof?: boolean;
  applicationId?: string;
  documents?: ApplicationDocument[];
  onGroupSelect: (data: boolean) => void;
}) => {
  const [renamePopupVisible, setRenamePopupVisible] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<
    ApplicationDocument | undefined
  >(undefined);
  const [documentName, setDocumentName] = useState('');
  const [nameError, setNameError] = useState('');

  const {
    setError,
    formState: { errors },
  } = useForm();

  const [
    getIndividualApplicationDocumentFile,
    { isLoading, isSuccess, isError },
  ] = useLazyGetIndividualApplicationDocumentFileQuery();
  const [
    getCofApplicationDocumentFile,
    {
      isLoading: cofDocIsLoading,
      isSuccess: cofDocIsSuccess,
      isError: cofDocIsError,
    },
  ] = useLazyGetCofApplicationDocumentFileQuery();
  const [rename, { isLoading: renameInProgress, isSuccess: renameIsSuccess }] =
    useRenameIndividualDocumentMutation();
  const [
    renameCofDocument,
    {
      isLoading: renameCofDocumentInProgress,
      isSuccess: renameCofDocumentIsSuccess,
    },
  ] = useRenameCofDocumentMutation();

  const onRename = async () => {
    if (documentName?.length < 3) {
      setNameError('name must be longer than or equal to 3 characters');
    } else {
      setNameError('');

      let response;

      if (cof) {
        response = (await renameCofDocument({
          id: applicationId as string,
          documentId: selectedDocument?.id as string,
          body: {
            name: documentName,
          },
        })) as { error?: ResponseError };
      } else {
        response = (await rename({
          id: applicationId as string,
          documentId: selectedDocument?.id as string,
          body: {
            name: documentName,
          },
        })) as { error?: ResponseError };
      }

      if (response.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
        });
      }
    }
  };

  const getFiles = async () => {
    const selectedDocuments = documents?.filter((item) => item.isSelected);

    if (selectedDocuments && selectedDocuments.length) {
      let downloadedFiles;

      if (cof) {
        downloadedFiles = await Promise.all(
          selectedDocuments.map(async (item) => {
            const response = await getCofApplicationDocumentFile({
              id: applicationId as string,
              documentId: item.id,
            });

            return response.data;
          }),
        );
      } else {
        downloadedFiles = await Promise.all(
          selectedDocuments.map(async (item) => {
            const response = await getIndividualApplicationDocumentFile({
              id: applicationId as string,
              documentId: item.id,
            });

            return response.data;
          }),
        );
      }

      if (downloadedFiles) {
        downloadedFiles.forEach((file, index) => {
          const hiddenElement = document.createElement('a');
          const url = window.URL || window.webkitURL;

          // @ts-ignore
          const blob = url.createObjectURL(file);
          hiddenElement.href = blob;
          hiddenElement.target = '_blank';
          hiddenElement.download = selectedDocuments[index].name;
          hiddenElement.click();
        });
      }
    }
  };

  useEffect(() => {
    if (selectedDocument && !documentName) {
      setDocumentName(selectedDocument.name);
    }
  }, [selectedDocument]);

  useEffect(() => {
    if (renameCofDocumentIsSuccess || renameIsSuccess) {
      setRenamePopupVisible(false);
      setSelectedDocument(undefined);
      setDocumentName('');
      setNameError('');
    }
  }, [renameCofDocumentIsSuccess, renameIsSuccess]);

  useEffect(() => {
    if (isSuccess || isError || cofDocIsSuccess || cofDocIsError) {
      onGroupSelect(false);
    }
  }, [isSuccess, isError, cofDocIsSuccess]);

  return {
    setSelectedDocument,
    setRenamePopupVisible,
    isLoading,
    cofDocIsLoading,
    getFiles,
    setDocumentName,
    setNameError,
    onRename,
    renameInProgress,
    renameCofDocumentInProgress,
    renamePopupVisible,
    selectedDocument,
    documentName,
    nameError,
    errors,
  };
};

export default useDocuments;
